'use client'
import { useGetAdsQuery } from '@/app/globalRedux/api/adsSlice'
import { RootState } from '@/app/globalRedux/store'
import {
  handleAdIntersection,
  useIntersectionObserver,
} from '@/app/hooks/useIntersectionObserver'
import { AdBannerScheduleType } from '@/app/types/adType'
import { getAdsIndex, handleAdClick } from '@/app/utils/AdsUtility'
import { useNavigation } from '@/app/utils/navigation'
import Image from 'next/image'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

const adBannerPage = (path: string): string => {
  //paths should be in the order from multiple names to single name to avoid confict between same substring confusion
  const PATHMAP = {
    'product-company-tab': 'Search Results Products',
    'company-tab': 'Search Results Companies',
    'product-tab': 'Search Results Offers',
    'news-tab': 'Search Results News',
    'retails/companies': 'Retail Chain Companies',
    'retails/news': 'Retail Chain News',
    'buyers-listing': 'Alerts Listing',
    buyers_alert: 'Alerts Detail',
    events: 'Events Listing',
    'news-detail': 'News Detail',
    news: 'News Listing',
    'sales-listing': 'Offers Listing',
    sales_offers: 'Offers Detail',
  }
  for (const [key, value] of Object.entries(PATHMAP)) {
    if (path.includes(key)) {
      return value
    }
  }
  return ''
}

const RightAdsPanel = () => {
  const params = useParams<{ lng: string }>()
  const pathName = usePathname()
  const { redirectToAdvertise } = useNavigation()
  const isNewsListing =
    pathName.includes('news') &&
    !pathName.includes('news-tab') &&
    !pathName.includes('news-detail') &&
    !pathName.includes('retails')
  const { data: adList } = useGetAdsQuery<{
    data: AdBannerScheduleType[]
    isLoading: boolean
    isUninitialized: boolean
    isError: boolean
    isFetching: boolean
  }>(
    { page: adBannerPage(pathName), lang: String(params.lng) },
    { skip: adBannerPage(pathName) == null }
  )
  const localeLangauge = useSelector(
    (state: RootState) => state.user.localeLangauge
  )
  const [adForPositionOne, setAdForPositionOne] =
    useState<AdBannerScheduleType>()
  const [adForPositionTwo, setAdForPositionTwo] =
    useState<AdBannerScheduleType>()
  const [adForPositionThree, setAdForPositionThree] =
    useState<AdBannerScheduleType>()
  const [adForPositionFour, setAdForPositionFour] =
    useState<AdBannerScheduleType>()

  useEffect(() => {
    if (adList) {
      setAdForPositionOne(adList[getAdsIndex(adList, 'Ad Position 1')])
      setAdForPositionTwo(adList[getAdsIndex(adList, 'Ad Position 2')])
      setAdForPositionThree(adList[getAdsIndex(adList, 'Ad Position 3')])
      setAdForPositionFour(adList[getAdsIndex(adList, 'Ad Position 4')])
    }
  }, [adList])

  const observeElement = useIntersectionObserver(handleAdIntersection)

  const adPositionOneRef = useRef<HTMLDivElement>(null)
  const adPositionTwoRef = useRef<HTMLDivElement>(null)
  const adPositionThreeRef = useRef<HTMLDivElement>(null)
  const adPositionFourRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (adPositionOneRef.current) {
      observeElement(adPositionOneRef.current)
    }
    if (adPositionTwoRef.current) {
      observeElement(adPositionTwoRef.current)
    }
    if (adPositionThreeRef.current) {
      observeElement(adPositionThreeRef.current)
    }
    if (adPositionFourRef.current) {
      observeElement(adPositionFourRef.current)
    }
  }, [
    adPositionOneRef,
    adPositionTwoRef,
    adForPositionThree,
    adForPositionFour,
    observeElement,
  ])
  return (
    <div className="flex flex-col gap-5 mobile:gap-[10px] mobile-width">
      {/* Ad Position 1 */}
      <div
        className="ad_banner_gtm flex w-[300px] h-[600px] cursor-pointer rounded-[3px] overflow-hidden mobile-width mobile:h-[auto] banner-carousel"
        ref={adPositionOneRef}
        data-ad-position="Ad Position 1"
        data-ad-id={adForPositionOne?.ad_banner?.id}
        data-ad-sid={adForPositionOne?.id}
        data-ad-page={adForPositionOne?.page?.name}
        data-ad-company-id={adForPositionOne?.ad_banner?.company?.id}
        data-ad-lang={params?.lng}
      >
        {adForPositionOne ? (
          <Image
            width={300}
            height={450}
            src={adForPositionOne?.ad_banner?.image}
            alt=""
            id="ad_click"
            className="object-cover w-full h-full"
            onClick={() => handleAdClick(adForPositionOne, params?.lng)}
          />
        ) : (
          <Image
            priority
            width={300}
            height={450}
            className="object-cover"
            src={`/assets/image/FructidorAds/${localeLangauge}/RightPanel/Ad1 300x600.png`}
            alt=""
            onClick={redirectToAdvertise}
          />
        )}
      </div>
      {/* Ad Position 2 */}
      <div
        className={`ad_banner_gtm flex ${isNewsListing ? 'w-[300px] h-[450px]' : 'w-[300px] h-[300px]'}  cursor-pointer rounded-[3px] overflow-hidden mobile-width mobile:h-[auto] banner-carousel`}
        ref={adPositionTwoRef}
        data-ad-position="Ad Position 2"
        data-ad-id={adForPositionTwo?.ad_banner?.id}
        data-ad-sid={adForPositionTwo?.id}
        data-ad-page={adForPositionTwo?.page?.name}
        data-ad-company-id={adForPositionTwo?.ad_banner?.company?.id}
        data-ad-lang={params?.lng}
      >
        {adForPositionTwo ? (
          <Image
            width={300}
            height={isNewsListing ? 450 : 300}
            src={adForPositionTwo?.ad_banner?.image}
            alt=""
            className="object-cover w-full h-full"
            id="ad_click"
            onClick={() => handleAdClick(adForPositionTwo, params?.lng)}
          />
        ) : (
          <Image
            priority
            width={300}
            height={300}
            className="object-cover"
            src={`/assets/image/FructidorAds/${localeLangauge}/RightPanel/${isNewsListing ? 'Ad2_300x450.png' : 'Ad2 300x300.png'}`}
            alt=""
            onClick={redirectToAdvertise}
          />
        )}
      </div>
      {/* Ad Position 3 */}
      <div
        className="ad_banner_gtm flex w-[300px] h-[300px] cursor-pointer rounded-[3px] overflow-hidden mobile-width mobile:h-[auto] banner-carousel"
        ref={adPositionThreeRef}
        data-ad-position="Ad Position 3"
        data-ad-id={adForPositionThree?.ad_banner?.id}
        data-ad-sid={adForPositionThree?.id}
        data-ad-page={adForPositionThree?.page?.name}
        data-ad-company-id={adForPositionThree?.ad_banner?.company?.id}
        data-ad-lang={params?.lng}
      >
        {adForPositionThree ? (
          <Image
            width={300}
            height={300}
            src={adForPositionThree?.ad_banner?.image}
            alt=""
            className="object-cover w-full h-full"
            id="ad_click"
            onClick={() => handleAdClick(adForPositionThree, params?.lng)}
          />
        ) : (
          <Image
            priority
            width={300}
            height={300}
            src={`/assets/image/FructidorAds/${localeLangauge}/RightPanel/${isNewsListing ? 'Ad2 300x300.png' : 'Ad3 300x300.png'}`}
            className="object-cover"
            alt=""
            onClick={redirectToAdvertise}
          />
        )}
      </div>
      {/* Ad Position 4 */}
      {!isNewsListing && (
        <div
          className="ad_banner_gtm flex w-[300px] h-[150px] cursor-pointer rounded-[3px] overflow-hidden mobile-width mobile:h-[auto] banner-carousel"
          ref={adPositionFourRef}
          data-ad-position="Ad Position 4"
          data-ad-id={adForPositionFour?.ad_banner?.id}
          data-ad-sid={adForPositionFour?.id}
          data-ad-page={adForPositionFour?.page?.name}
          data-ad-company-id={adForPositionFour?.ad_banner?.company?.id}
          data-ad-lang={params?.lng}
        >
          {adForPositionFour ? (
            <Image
              width={300}
              height={150}
              src={adForPositionFour?.ad_banner?.image}
              alt=""
              className="object-cover w-full h-full"
              id="ad_click"
              onClick={() => handleAdClick(adForPositionFour, params?.lng)}
            />
          ) : (
            <Image
              priority
              width={300}
              height={150}
              className="object-cover"
              src={`/assets/image/FructidorAds/${localeLangauge}/RightPanel/Ad4 300x150.png`}
              alt=""
              onClick={redirectToAdvertise}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default RightAdsPanel
